<template>
  <div
    class="finantial-settings-payout-schedule-page"
    v-loading="loading"
  >
    <div class="finantial-settings-payout-schedule-page__card__info">
      <app-i18n code="subpropertySettings.finantial.payoutSchedule.cardInfo" />
    </div>
    <div class="finantial-settings-payout-schedule-page__card__frequency">
      <app-i18n
        class="finantial-settings-payout-schedule-page__card__frequency__title"
        code="subpropertySettings.finantial.payoutSchedule.cardFrequency.title"
      />
      <el-form
        class="form"
        ref="form"
        :model="model"
        :rules="rules"
        @submit.native.prevent="doSubmit"
      >
        <el-row :gutter="15">
          <!-- Payout -->
          <el-col :lg="8" :md="8" :sm="24">
            <el-form-item
              :label="i18n('subpropertySettings.finantial.payoutSchedule.cardFrequency.fields.payout')"
            >
              <el-select
                :placeholder="i18n('subpropertySettings.finantial.payoutSchedule.cardFrequency.placeholders.automatic')"
                :disabled="true"
                value
              >
                <i class="icon-calendar" slot="prefix" />
              </el-select>
            </el-form-item>
          </el-col>

          <!-- Frequency -->
          <el-col :lg="8" :md="8" :sm="24">
            <el-form-item 
              :label="fields.payoutsScheduleInterval.label"
              :prop="fields.payoutsScheduleInterval.name"
              :required="fields.payoutsScheduleInterval.required"
            >
              <el-select 
                :placeholder="fields.payoutsScheduleInterval.label"
                v-model="model.payoutsScheduleInterval"
              >
                <i class="icon-calendar" slot="prefix" />
                <el-option
                  :key="option.id"
                  :label="option.label"
                  :value="option.id"
                  v-for="option in fields.payoutsScheduleInterval.options"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <!-- Anchor -->
          <el-col :lg="8" :md="8" :sm="24">
            <el-form-item
              v-if="anchorField"
              :label="anchorField.label"
              :prop="anchorField.name"
              :required="anchorField.required"
            >
              <el-select
                :disabled="model.payoutsScheduleInterval === 'daily'"
                :placeholder="anchorField.label"
                v-model="model[anchorField.name]"
              >
                <i class="icon-calendar" slot="prefix" />
                <el-option
                  :key="option.id"
                  :label="option.label"
                  :value="option.id"
                  v-for="option in anchorField.options"
                />
              </el-select>

            </el-form-item>
          </el-col>

          <!-- Form Button SAVE -->
          <el-col :lg="24" :md="24" :sm="24">
            <el-form-item class="finantial-settings-payout-schedule-page__card__frequency__form__button">
              <el-button
                type="secondary"
                :disabled="loading"
                @click="doSubmit"
              >
                <app-i18n code="common.save" />
              </el-button>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { i18n } from '@shared/i18n';
import { FormSchema } from '@shared/form/form-schema';
import { FinancialSettingsPayoutScheduleModel } from '@modules/subproperty-settings/finantial-settings/finantial-settings-payout-schedule/finantial-settings-payout-schedule-model';
import { get } from 'lodash';
import cleanNilData from '@shared/utils/cleanNilData';

const { fields } = FinancialSettingsPayoutScheduleModel;
const formSchema = new FormSchema([
  fields.payoutsScheduleInterval,
  fields.payoutsScheduleMonthlyAnchor,
  fields.payoutsScheduleWeeklyAnchor,
]);

export default {
  name: 'finantial-settings-payout-schedule-page',

  props: {
    prevRoute: Object,
  },

  data() {
    return {
      rules: formSchema.rules(),
      model: {},
    };
  },

  computed: {
    ...mapGetters({
      currentManage: 'auth/manage/currentManage',
      loading: 'paymentSettings/payoutSchedule/loading',
      settings: 'paymentSettings/payoutSchedule/settings',
      connectedAccountId: 'paymentSettings/payoutSchedule/connectedAccountId',
      stripeConnectedAccountId: 'paymentSettings/payoutSchedule/stripeConnectedAccountId',
      isRootScreen: 'layout/isRootScreen',
    }),
    fields() {
      return fields;
    },
    propertyId() {
      return get(this.currentManage, 'property.id', null);
    },
    subpropertyId() {
      return get(
        this.currentManage,
        'subproperty.id',
        null,
      );
    },
    anchorField() {
      if (this.model) {
        const { payoutsScheduleInterval } = this.model;
        const {
          payoutsScheduleWeeklyAnchor,
          payoutsScheduleMonthlyAnchor,
        } = fields;

        if (payoutsScheduleInterval === 'daily') {
          return null;
        } else if (payoutsScheduleInterval === 'weekly') {
          return payoutsScheduleWeeklyAnchor;
        } else if (payoutsScheduleInterval === 'monthly') {
          return payoutsScheduleMonthlyAnchor;
        }
      }
      return null;
    },
  },

  created() {
    this.model = formSchema.initialValues(this.settings);
  },

  async mounted() {
    if (this.subpropertyId) {
      await this.doFetch({
        subpropertyId: this.subpropertyId,
      });
    }
    this.model = formSchema.initialValues(this.settings);
  },

  methods: {
    ...mapActions({
      doFetch: 'paymentSettings/payoutSchedule/doFetch',
      doUpdate: 'paymentSettings/payoutSchedule/doUpdate',
    }),

    async doSubmit() {
      try {
        await this.$refs.form.validate();
      } catch (error) {
        return;
      }

      const { ...data } = formSchema.cast(this.model);

      await this.doUpdate(cleanNilData({
        propertyId: this.propertyId,
        connectedAccountId: this.connectedAccountId,
        values: data,
        stripeConnectedAccountId: this.stripeConnectedAccountId,
      }));

      if (this.isRootScreen || (this.prevRoute && this.prevRoute.name !== 'subproperty')) return;

      this.$router.push({ name: 'home' });
    },
    i18n(code) {
      return i18n(code);
    },
  },

  watch: {
    'model.payoutsScheduleInterval'(newVal) {
      if (newVal === 'daily') {
        this.model.payoutsScheduleWeeklyAnchor = null;
        this.model.payoutsScheduleMonthlyAnchor = null;
      }
      if (newVal === 'weekly') {
        this.model.payoutsScheduleMonthlyAnchor = null;
      }
      if (newVal === 'monthly') {
        this.model.payoutsScheduleWeeklyAnchor = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.finantial-settings-payout-schedule-page {
  color: #4c4c4c;
  padding: 10px 20px;

  &__card {
    &__info {
      padding: 24px 32px;
      margin-bottom: 24px;
      background-color: #f7f7f7;
      color: #999999;
    }

    &__frequency {
      padding: 36px 32px 12px 32px;
      box-shadow: -10px 10px 20px 0 rgba(30, 30, 30, 0.05);

      &__title {
        text-transform: uppercase;
        font-weight: bold;
        padding-bottom: 28px;
      }

      &__form__button {
        display: grid;
        justify-content: end;
        padding-top: 12px;
      }
    }
  }
}
</style>

<style>
.finantial-settings-payout-schedule-page .el-form {
  padding: 28px 0 0;
}
</style>